import React, { useEffect, useMemo, useState } from 'react';
import {
  addStoreToSubArea,
  createSubArea,
  deleteDistrict,
  deleteSubArea,
  enableDistrict,
  fetchAllDistricts,
  fetchAllStores,
  fetchAllSubAreas,
  removeStoreFromSubArea,
  updateSubArea,
} from '../../../Services/adminApiService';
import '../UserManagement/UserManagement.css';

const emptyForm = {
  id: null,
  name: '',
  districtId: '',
  disabled: false,
};

const mergeById = (previousItems, nextItems) => {
  const merged = new Map();
  previousItems.forEach((item) => {
    if (item?.id !== undefined && item?.id !== null) {
      merged.set(item.id, item);
    }
  });
  nextItems.forEach((item) => {
    if (item?.id !== undefined && item?.id !== null) {
      merged.set(item.id, {
        ...(merged.get(item.id) || {}),
        ...item,
      });
    }
  });
  return Array.from(merged.values());
};

const getErrorMessage = (error, fallback) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.apiError?.errorMessage ||
    error?.message ||
    fallback
  );
};

const getDistrictLabel = (district) => {
  if (!district) return '—';
  const storeParts = [district.storeName, district.storeNum].filter(Boolean);
  const storeLabel = storeParts.length > 0 ? ` • ${storeParts.join(' #')}` : '';
  return `${district.districtName || 'District'}${storeLabel}`;
};

const DistrictManagement = () => {
  const [districts, setDistricts] = useState([]);
  const [districtCache, setDistrictCache] = useState([]);
  const [, setSubAreas] = useState([]);
  const [subAreaCache, setSubAreaCache] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [selectedStoreBySubArea, setSelectedStoreBySubArea] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [districtResponse, subAreaResponse, storeResponse] =
        await Promise.allSettled([
          fetchAllDistricts(),
          fetchAllSubAreas(),
          fetchAllStores(),
        ]);

      const nextDistricts =
        districtResponse.status === 'fulfilled' &&
        Array.isArray(districtResponse.value)
          ? districtResponse.value
          : [];
      const nextSubAreas =
        subAreaResponse.status === 'fulfilled' &&
        Array.isArray(subAreaResponse.value)
          ? subAreaResponse.value
          : [];
      const nextStores =
        storeResponse.status === 'fulfilled' &&
        Array.isArray(storeResponse.value)
          ? storeResponse.value
          : [];

      setDistricts(nextDistricts);
      setDistrictCache((previous) => mergeById(previous, nextDistricts));
      setSubAreas(nextSubAreas);
      setSubAreaCache((previous) => mergeById(previous, nextSubAreas));
      setStores(nextStores);
    } catch (error) {
      console.error('Failed to load district data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!selectedDistrictId && districts.length > 0) {
      setSelectedDistrictId(String(districts[0].id));
    }
  }, [districts, selectedDistrictId]);

  const selectedDistrict = useMemo(() => {
    if (!selectedDistrictId) {
      return null;
    }
    return districtCache.find(
      (district) => String(district.id) === String(selectedDistrictId)
    );
  }, [districtCache, selectedDistrictId]);

  const selectedDistrictSubAreas = useMemo(() => {
    if (!selectedDistrictId) {
      return [];
    }
    return subAreaCache.filter(
      (subArea) => String(subArea.district?.id) === String(selectedDistrictId)
    );
  }, [subAreaCache, selectedDistrictId]);

  const storeLabel = (store) => {
    return (
      store?.storeNum || store?.name || store?.storeName || `Store ${store?.id}`
    );
  };

  const storesForSubArea = (subArea) => {
    return stores.filter(
      (store) => String(store.subArea?.id) === String(subArea.id)
    );
  };

  const availableStoresForSubArea = (subArea) => {
    const districtId = subArea.district?.id;
    if (!districtId || subArea.disabled || subArea.district?.disabled) {
      return [];
    }
    return stores.filter((store) => {
      if (store.disabled) {
        return false;
      }
      if (String(store.district?.id) !== String(districtId)) {
        return false;
      }
      return !store.subArea || String(store.subArea.id) === String(subArea.id);
    });
  };

  const resetForm = () => {
    setFormData(emptyForm);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSelectDistrict = (districtId) => {
    setSelectedDistrictId(districtId);
    if (!formData.id) {
      setFormData((previous) => ({
        ...previous,
        districtId: districtId || previous.districtId,
      }));
    }
  };

  const handleEditSubArea = (subArea) => {
    setFormData({
      id: subArea.id,
      name: subArea.name || '',
      districtId: subArea.district?.id ? String(subArea.district.id) : '',
      disabled: !!subArea.disabled,
    });
    if (subArea.district?.id) {
      setSelectedDistrictId(String(subArea.district.id));
    }
  };

  const handleDistrictStatusToggle = async (district) => {
    if (!district) return;

    if (!district.disabled) {
      const confirmed = window.confirm(
        'Disabling this district will cascade to all sub-areas in the district and disassociate their stores. Continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    try {
      if (district.disabled) {
        await enableDistrict(district.id);
      } else {
        await deleteDistrict(district.id);
      }

      setDistrictCache((previous) =>
        previous.map((item) =>
          item.id === district.id
            ? { ...item, disabled: !district.disabled }
            : item
        )
      );

      setSubAreaCache((previous) =>
        previous.map((subArea) =>
          String(subArea.district?.id) === String(district.id)
            ? { ...subArea, disabled: !district.disabled }
            : subArea
        )
      );

      await loadData();
      alert(
        district.disabled
          ? 'District re-enabled successfully.'
          : 'District disabled successfully.'
      );
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to update district status.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.name.trim() || !formData.districtId) {
      alert('Sub-area name and district are required.');
      return;
    }

    const district = districtCache.find(
      (item) => String(item.id) === String(formData.districtId)
    );

    if (!district || district.disabled) {
      alert('Select an active district before saving a sub-area.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        district: { id: Number(formData.districtId) },
        disabled: !!formData.disabled,
      };

      if (formData.id) {
        await updateSubArea(formData.id, payload);
        alert('Sub-area updated successfully.');
      } else {
        await createSubArea(payload);
        alert('Sub-area created successfully.');
      }

      resetForm();
      await loadData();
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to save sub-area.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDisableSubArea = async (subArea) => {
    if (
      !window.confirm('Disable this sub-area? Stores will be disassociated.')
    ) {
      return;
    }

    setSaving(true);
    try {
      await deleteSubArea(subArea.id);
      setSubAreaCache((previous) =>
        previous.map((item) =>
          item.id === subArea.id ? { ...item, disabled: true } : item
        )
      );
      await loadData();
      if (formData.id === subArea.id) {
        resetForm();
      }
      alert('Sub-area disabled successfully.');
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to disable sub-area.'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnableSubArea = async (subArea) => {
    const district = districtCache.find(
      (item) => String(item.id) === String(subArea.district?.id)
    );
    if (district?.disabled) {
      alert('Enable the district first before re-enabling this sub-area.');
      return;
    }

    setSaving(true);
    try {
      await updateSubArea(subArea.id, {
        name: subArea.name,
        district: { id: Number(subArea.district?.id) },
        disabled: false,
      });
      setSubAreaCache((previous) =>
        previous.map((item) =>
          item.id === subArea.id ? { ...item, disabled: false } : item
        )
      );
      await loadData();
      alert('Sub-area re-enabled successfully.');
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to enable sub-area.'));
    } finally {
      setSaving(false);
    }
  };

  const handleStoreSelectionChange = (subAreaId, value) => {
    setSelectedStoreBySubArea((previous) => ({
      ...previous,
      [subAreaId]: value,
    }));
  };

  const handleAddStore = async (subArea) => {
    const storeId = selectedStoreBySubArea[subArea.id];
    if (!storeId) {
      return;
    }

    try {
      await addStoreToSubArea(subArea.id, Number(storeId));
      setSelectedStoreBySubArea((previous) => ({
        ...previous,
        [subArea.id]: '',
      }));
      await loadData();
      alert('Store assigned to sub-area.');
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to assign store to sub-area.'));
    }
  };

  const handleRemoveStore = async (subAreaId, storeId) => {
    try {
      await removeStoreFromSubArea(subAreaId, storeId);
      await loadData();
      alert('Store removed from sub-area.');
    } catch (error) {
      alert(getErrorMessage(error, 'Failed to remove store from sub-area.'));
    }
  };

  const currentDistrictLabel = getDistrictLabel(selectedDistrict);

  return (
    <div className="user-management">
      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading districts...</p>
        </div>
      )}

      <div className="management-header">
        <h1>District Management</h1>
        <p className="management-subtitle">
          Manage districts and the nested sub-areas they own
        </p>
      </div>

      <div className="section-card">
        <div className="section-header">
          <h2>Districts ({districts.length})</h2>
          <button
            className="btn-secondary"
            onClick={loadData}
            disabled={saving || loading}
          >
            Refresh
          </button>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>District</th>
                <th>Linked Store</th>
                <th>Sub-Areas</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {districts.length > 0 ? (
                districts.map((district) => {
                  const districtSubAreaCount = subAreaCache.filter(
                    (subArea) =>
                      String(subArea.district?.id) === String(district.id)
                  ).length;

                  return (
                    <tr key={district.id}>
                      <td>
                        <div>
                          <strong>{district.districtName}</strong>
                          <div style={{ color: '#718096', fontSize: '12px' }}>
                            {district.id}
                          </div>
                        </div>
                      </td>
                      <td>
                        {district.storeName || district.storeNum ? (
                          <div>
                            <div>{district.storeName || 'Store'}</div>
                            <div style={{ color: '#718096', fontSize: '12px' }}>
                              {district.storeNum || '—'}
                              {district.storeCity
                                ? ` • ${district.storeCity}`
                                : ''}
                            </div>
                          </div>
                        ) : (
                          <span
                            style={{ color: '#718096', fontStyle: 'italic' }}
                          >
                            No store linked
                          </span>
                        )}
                      </td>
                      <td>{districtSubAreaCount}</td>
                      <td>
                        {district.disabled ? (
                          <span className="permission-badge dm">Disabled</span>
                        ) : (
                          <span className="permission-badge office">
                            Active
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() =>
                              handleSelectDistrict(String(district.id))
                            }
                          >
                            View
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDistrictStatusToggle(district)}
                            disabled={saving}
                          >
                            {district.disabled ? 'Enable' : 'Disable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="5"
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#718096',
                    }}
                  >
                    No districts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="section-card">
        <div className="section-header">
          <h2>
            {selectedDistrict ? currentDistrictLabel : 'District Details'}
          </h2>
          {selectedDistrict && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {selectedDistrict.disabled ? (
                <span className="permission-badge dm">Disabled</span>
              ) : (
                <span className="permission-badge office">Active</span>
              )}
              <button
                className="btn-delete"
                onClick={() => handleDistrictStatusToggle(selectedDistrict)}
                disabled={saving}
              >
                {selectedDistrict.disabled
                  ? 'Enable District'
                  : 'Disable District'}
              </button>
            </div>
          )}
        </div>

        {selectedDistrict ? (
          <>
            <div style={{ marginBottom: '16px', color: '#4a5568' }}>
              {selectedDistrict.disabled
                ? 'This district is disabled. Its sub-areas are also disabled by cascade.'
                : 'Manage the sub-areas under this district and assign stores to them.'}
            </div>

            <div className="section-card" style={{ marginBottom: '0' }}>
              <div className="section-header">
                <h3>{formData.id ? 'Edit Sub-Area' : 'Create Sub-Area'}</h3>
                {formData.id && (
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={resetForm}
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="name" className="required">
                      Sub-Area Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={selectedDistrict?.disabled}
                      placeholder="Enter sub-area name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="districtId" className="required">
                      District
                    </label>
                    <select
                      id="districtId"
                      name="districtId"
                      value={formData.districtId}
                      onChange={handleInputChange}
                      required
                      disabled={selectedDistrict?.disabled && !formData.id}
                    >
                      <option value="">Select a district</option>
                      {districtCache.map((district) => (
                        <option key={district.id} value={district.id}>
                          {district.districtName}
                          {district.disabled ? ' (Disabled)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="disabled">Disabled</label>
                    <select
                      id="disabled"
                      name="disabled"
                      value={formData.disabled ? 'true' : 'false'}
                      onChange={(event) =>
                        setFormData((previous) => ({
                          ...previous,
                          disabled: event.target.value === 'true',
                        }))
                      }
                      disabled={selectedDistrict?.disabled}
                    >
                      <option value="false">Active</option>
                      <option value="true">Disabled</option>
                    </select>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={saving || selectedDistrict?.disabled}
                  >
                    {saving
                      ? 'Saving...'
                      : formData.id
                      ? 'Update Sub-Area'
                      : 'Create Sub-Area'}
                  </button>
                  {formData.id && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={resetForm}
                    >
                      Clear Form
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div style={{ marginTop: '20px' }}>
              <div className="section-header" style={{ marginBottom: '12px' }}>
                <h3>Sub-Areas ({selectedDistrictSubAreas.length})</h3>
                <span style={{ color: '#718096', fontSize: '12px' }}>
                  District ID {selectedDistrict.id}
                </span>
              </div>

              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Stores</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedDistrictSubAreas.length > 0 ? (
                      selectedDistrictSubAreas.map((subArea) => {
                        const assignedStores = storesForSubArea(subArea);
                        const availableStores =
                          availableStoresForSubArea(subArea);
                        const districtDisabled = !!subArea.district?.disabled;
                        return (
                          <tr key={subArea.id}>
                            <td>{subArea.name}</td>
                            <td>
                              <div style={{ marginBottom: '12px' }}>
                                {assignedStores.length > 0 ? (
                                  assignedStores.map((store) => (
                                    <span
                                      key={store.id}
                                      className="username-badge"
                                      style={{
                                        display: 'inline-flex',
                                        gap: '8px',
                                        alignItems: 'center',
                                        marginRight: '8px',
                                        marginBottom: '8px',
                                      }}
                                    >
                                      {storeLabel(store)}
                                      <button
                                        type="button"
                                        className="btn-delete"
                                        onClick={() =>
                                          handleRemoveStore(
                                            subArea.id,
                                            store.id
                                          )
                                        }
                                        style={{ padding: '4px 8px' }}
                                        disabled={
                                          districtDisabled || subArea.disabled
                                        }
                                      >
                                        Remove
                                      </button>
                                    </span>
                                  ))
                                ) : (
                                  <span
                                    style={{
                                      color: '#718096',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    No stores assigned
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                }}
                              >
                                <select
                                  value={
                                    selectedStoreBySubArea[subArea.id] || ''
                                  }
                                  onChange={(event) =>
                                    handleStoreSelectionChange(
                                      subArea.id,
                                      event.target.value
                                    )
                                  }
                                  style={{ minWidth: '220px' }}
                                  disabled={
                                    districtDisabled || subArea.disabled
                                  }
                                >
                                  <option value="">
                                    Add store to sub-area
                                  </option>
                                  {availableStores
                                    .filter(
                                      (store) =>
                                        !store.subArea ||
                                        String(store.subArea.id) !==
                                          String(subArea.id)
                                    )
                                    .map((store) => (
                                      <option key={store.id} value={store.id}>
                                        {storeLabel(store)}
                                      </option>
                                    ))}
                                </select>
                                <button
                                  type="button"
                                  className="btn-secondary"
                                  onClick={() => handleAddStore(subArea)}
                                  disabled={
                                    districtDisabled || subArea.disabled
                                  }
                                >
                                  Add
                                </button>
                              </div>
                              {districtDisabled && (
                                <div
                                  style={{
                                    color: '#718096',
                                    fontSize: '12px',
                                    marginTop: '4px',
                                  }}
                                >
                                  Stores cannot be assigned while the parent
                                  district is disabled.
                                </div>
                              )}
                            </td>
                            <td>
                              {subArea.disabled || districtDisabled ? (
                                <span className="permission-badge dm">
                                  Disabled
                                </span>
                              ) : (
                                <span className="permission-badge office">
                                  Active
                                </span>
                              )}
                              {districtDisabled && (
                                <div
                                  style={{
                                    color: '#718096',
                                    fontSize: '12px',
                                    marginTop: '4px',
                                  }}
                                >
                                  Disabled via district cascade
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn-edit"
                                  onClick={() => handleEditSubArea(subArea)}
                                  disabled={districtDisabled}
                                >
                                  Edit
                                </button>
                                {subArea.disabled ? (
                                  <button
                                    className="btn-secondary"
                                    onClick={() => handleEnableSubArea(subArea)}
                                    disabled={saving || districtDisabled}
                                  >
                                    Enable
                                  </button>
                                ) : (
                                  <button
                                    className="btn-delete"
                                    onClick={() =>
                                      handleDisableSubArea(subArea)
                                    }
                                    disabled={saving}
                                  >
                                    Disable
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          style={{
                            textAlign: 'center',
                            padding: '20px',
                            color: '#718096',
                          }}
                        >
                          No sub-areas found for this district
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div style={{ color: '#718096' }}>
            Select a district to view its sub-areas.
          </div>
        )}
      </div>
    </div>
  );
};

export default DistrictManagement;
